export const typeformValidation = {
  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentValue]
    const inputs = currentStep.querySelectorAll("input[required], select[required], textarea[required]")
    let valid = true

    const validatedRadioNames = new Set()
    inputs.forEach((input) => {
      if (input.type === "radio") {
        if (!validatedRadioNames.has(input.name)) {
          validatedRadioNames.add(input.name)
          const radios = Array.from(currentStep.querySelectorAll("input[type='radio']")).filter((radio) => radio.name === input.name)
          const anyChecked = radios.some((radio) => radio.checked)
          if (!anyChecked) {
            valid = false
            const first = radios[0]
            if (first) this.showFieldError(first, this.t("errors.select_option", "Please select an option"))
          } else {
            radios.forEach((radio) => this.clearFieldError(radio))
          }
        }
      } else if (input.type === "checkbox") {
        if (!input.checked) {
          valid = false
          this.showFieldError(input, this.t("errors.required", "This field is required"))
        } else {
          this.clearFieldError(input)
        }
      } else if (!input.value.trim()) {
        valid = false
        this.showFieldError(input, this.t("errors.required", "This field is required"))
      } else {
        this.clearFieldError(input)
      }
    })

    if (!valid) return false
    if (!this.validateUploadStep(currentStep)) return false
    if (!this.validateScreenShareStep(currentStep)) return false
    return this.validateCriteriaForStep(this.currentValue, currentStep)
  },

  validateUploadStep(currentStep) {
    if (this.currentValue !== 3) return true
    if (this.selectedValidationMethod() !== "document_upload") return true

    const gate = this.activeUploadValidationGate(currentStep)
    gate?.querySelector(".field-error")?.remove()

    const files = this.hasSupportingDocumentsInputTarget ? this.supportingDocumentsInputTarget.files : null
    if (files && files.length > 0) return true

    if (gate) {
      const errorEl = document.createElement("p")
      errorEl.className = "field-error rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
      errorEl.textContent = this.t("errors.upload_file_required", "Choose at least one file before continuing.")
      gate.appendChild(errorEl)
    }
    return false
  },

  activeUploadValidationGate() {
    const method = this.selectedValidationMethod()
    const flow = this.uploadFlowTargets.find((element) => element.dataset.uploadFlow === method)
    return flow?.querySelector("[data-upload-validation-gate]") || null
  },

  validateScreenShareStep(currentStep) {
    if (this.currentValue !== 3) return true
    if (this.selectedValidationMethod() !== "screen_share_capture") return true

    const completeField = currentStep.querySelector('[name="status_match_application[screen_capture_llm_last][complete]"]')
    const gate = currentStep.querySelector("[data-screen-capture-target~='validationGate']")
    const complete = completeField?.value === "true"
    gate?.querySelector(".field-error")?.remove()

    if (complete) return true

    if (gate) {
      const errorEl = document.createElement("p")
      errorEl.className = "field-error text-red-500 text-sm mt-2"
      errorEl.textContent = this.t("errors.screen_share_incomplete", "Capture your full name, airline program membership number, and current status from one or more screen-sharing pages before continuing.")
      gate.appendChild(errorEl)
    }
    return false
  },

  validateCriteriaForStep(stepIndex, currentStep) {
    const criteria = this.criteriaValue
    if (!criteria || typeof criteria !== "object" || Object.keys(criteria).length === 0) return true

    if (stepIndex === 0) {
      const allowed = criteria.allowed_resident_countries
      if (Array.isArray(allowed) && allowed.length > 0) {
        const select = currentStep.querySelector('select[name="status_match_application[country_of_residence]"]')
        if (select) {
          this.clearFieldError(select)
          if (!select.value || !allowed.includes(select.value)) {
            this.showFieldError(select, this.t("errors.country_not_eligible", "Your country is not eligible for this campaign."))
            return false
          }
        }
      }

      const email = currentStep.querySelector('input[name="status_match_application[email]"]')
      if (email?.value.trim()) {
        this.clearFieldError(email)
        if (!this.isValidEmail(email.value)) {
          this.showFieldError(email, this.t("errors.invalid_email", "Please enter a valid email address."))
          return false
        }
      }
    }

    if (stepIndex === 1) {
      if (criteria.member_number_regex) {
        const regex = this.rubyRegexToJs(criteria.member_number_regex)
        const memberNumberFieldName =
          criteria.member_number_field_name || "status_match_application[member_number]"
        const input = currentStep.querySelector(`input[name="${memberNumberFieldName}"]`)
        if (regex && input) {
          this.clearFieldError(input)
          const value = input.value.trim()
          if (!value || !regex.test(value)) {
            this.showFieldError(
              input,
              criteria.member_number_message || this.t("errors.member_number_format", "Enter a valid membership number in the expected format.")
            )
            return false
          }
        }
      }

      const select = currentStep.querySelector('select[name="status_match_application[origin_tier]"]')
      if (select) {
        const pairs = criteria.allowed_airline_tier_pairs
        this.clearFieldError(select)

        if (Array.isArray(pairs) && pairs.length > 0) {
          // The programme is free-form and is resolved server-side by the LLM.
          // At this stage, only require a tier offered by at least one campaign.
          const eligible = pairs.some((pair) => pair.tier === select.value)
          if (!select.value || !eligible) {
            this.showFieldError(select, this.t("errors.tier_not_eligible", "This status is not eligible for this offer."))
            return false
          }
        }
      }
    }

    return true
  },

  isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  },

  rubyRegexToJs(pattern) {
    if (!pattern || typeof pattern !== "string") return null
    try {
      const converted = pattern.replace(/\\A/g, "^").replace(/\\z/g, "$").replace(/\\Z/g, "$")
      return new RegExp(converted)
    } catch (error) {
      console.warn("typeform: invalid member_number_regex", error)
      return null
    }
  },

  showFieldError(input, message) {
    input.classList.add("input-error", "border-red-500")
    let errorEl = input.parentElement.querySelector(".field-error")
    if (!errorEl) {
      errorEl = document.createElement("p")
      errorEl.className = "field-error text-red-500 text-sm mt-1"
      input.parentElement.appendChild(errorEl)
    }
    errorEl.textContent = message
  },

  clearFieldError(input) {
    input.classList.remove("input-error", "border-red-500")
    input.parentElement.querySelector(".field-error")?.remove()
  }
}
